import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Visibility, GetApp, Lock, ChevronLeft, ChevronRight, Refresh, Receipt, Close } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import expensesService from '../../services/expensesService';

const parseAmount = (value) => {
  const amount = Number.parseFloat(value);
  return Number.isFinite(amount) ? amount : 0;
};

const formatAmount = (value) => parseAmount(value).toLocaleString(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatOriginalAmountTotals = (rows) => {
  const totalsByCurrency = rows.reduce((acc, expense) => {
    const currency = expense.currency || 'Amount';
    acc[currency] = (acc[currency] || 0) + parseAmount(expense.original_amount);
    return acc;
  }, {});

  const entries = Object.entries(totalsByCurrency);
  if (!entries.length) {
    return '0.00';
  }
  return entries.map(([currency, total]) => `${currency} ${formatAmount(total)}`).join(' + ');
};

const ExpenseList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState(null);

  // Pagination state matching Users/index.js
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);

  const totalPages = Math.ceil(totalCount / rowsPerPage) || 1;

  const [filters, setFilters] = useState({
    search: '',
    status: '',
    expense_date_gte: '',
    expense_date_lte: ''
  });
  
  // Export Dialog State
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState('csv');

  // Receipt Preview State
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');

  const handleOpenPreview = (url) => {
    if (url) {
      setPreviewUrl(url);
      setPreviewOpen(true);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewUrl('');
  };

  const fetchExpenses = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        page: page + 1,
        page_size: rowsPerPage,
        ...filters
      };
      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '') delete params[key];
      });

      const data = await expensesService.getExpenses(params);
      setExpenses(data.results);
      setTotalCount(data.count);
    } catch (err) {
      console.error('Failed to fetch expenses', err);
      if (err.response?.status === 403) {
        setError('You do not have permission to view expenses.');
      } else {
        setError('Failed to fetch expenses. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleApplyFilters = () => {
    setPage(0); // Reset to first page
    fetchExpenses();
  };

  const handleRefresh = () => {
    setPage(0);
    fetchExpenses();
  };

  // Pagination handlers
  const handlePrevPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };
  const handleNextPage = () => {
    if (page < totalPages - 1) {
      setPage(page + 1);
    }
  };
  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(rowsPerPage.toString());
    } else {
      setRowsPerPage(Number(value));
      setPage(0);
      setCustomRowsActive(false);
    }
  };
  const handleCustomRowsInputChange = (e) => {
    setCustomRowsInput(e.target.value.replace(/\D/g, ''));
  };
  const handleCustomRowsInputBlur = () => {
    const val = parseInt(customRowsInput, 10);
    if (val && val > 0) {
      setRowsPerPage(val);
      setPage(0);
    }
    setCustomRowsActive(false);
  };

  const handleExport = async () => {
    try {
      const params = { ...filters };
      Object.keys(params).forEach(key => {
        if (params[key] === '') delete params[key];
      });

      let data;
      if (exportFormat === 'csv') {
        data = await expensesService.exportCsv(params);
      } else {
        data = await expensesService.exportExcel(params);
      }

      // Create download link
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `expenses_export_${new Date().toISOString()}.${exportFormat === 'csv' ? 'csv' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setExportDialogOpen(false);
    } catch (err) {
      console.error('Export failed', err);
    }
  };

  const pageTotals = useMemo(() => ({
    originalAmount: formatOriginalAmountTotals(expenses),
    amountInNgn: expenses.reduce((total, expense) => total + parseAmount(expense.amount_in_ngn), 0),
    amountInCny: expenses.reduce((total, expense) => total + parseAmount(expense.amount_in_cny), 0),
  }), [expenses]);

  const columns = [
    {
      field: 'reference_code',
      headerName: 'Reference',
      minWidth: 150,
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {params.value}
          {params.row.is_locked && (
            <Tooltip title="Locked Period">
              <Lock fontSize="small" color="action" sx={{ ml: 1 }} />
            </Tooltip>
          )}
        </Box>
      )
    },

    {
      field: 'title',
      headerName: 'Title',
      minWidth: 200,
      flex: 1.5
    },
    {
      field: 'category',
      headerName: 'Category',
      minWidth: 150,
      flex: 1
    },
    {
      field: 'original_amount',
      headerName: 'Amount',
      minWidth: 150,
      flex: 1,
      renderCell: (params) => (
        <span>{params.row.currency} {parseFloat(params.value).toLocaleString()}</span>
      )
    },
    {
      field: 'amount_in_ngn',
      headerName: 'Amount (NGN)',
      minWidth: 150,
      flex: 1,
      renderCell: (params) => (
        <span>₦{parseFloat(params.value).toLocaleString()}</span>
      )
    },
    {
      field: 'amount_in_cny',
      headerName: 'Amount (CNY)',
      minWidth: 150,
      flex: 1,
      renderCell: (params) => (
        <span>¥{params.value ? parseFloat(params.value).toLocaleString() : '0.00'}</span>
      )
    },
    {
      field: 'expense_date',
      headerName: 'Expense Date',
      minWidth: 140,
      flex: 1,
      renderCell: (params) => (
        <span>{params.value ? format(new Date(params.value), 'yyyy-MM-dd') : '-'}</span>
      )
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      minWidth: 180,
      flex: 1,
      renderCell: (params) => (
        <span>{params.value ? format(new Date(params.value), 'yyyy-MM-dd HH:mm') : '-'}</span>
      )
    },
    {
      field: 'updated_at',
      headerName: 'Updated At',
      minWidth: 180,
      flex: 1,
      renderCell: (params) => (
        <span>{params.value ? format(new Date(params.value), 'yyyy-MM-dd HH:mm') : '-'}</span>
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 120,
      flex: 1,
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          color={params.value === 'approved' ? 'success' : params.value === 'rejected' ? 'error' : 'warning'}
          size="small" 
          sx={{ fontWeight: 'bold', color: 'white' }}
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View Details">
            <IconButton onClick={() => navigate(`/admin/expenses/${params.row.id}`)} color="primary">
              <Visibility />
            </IconButton>
          </Tooltip>
          {params.row.receipt && (
            <Tooltip title="View Receipt">
              <IconButton onClick={() => handleOpenPreview(params.row.receipt)} color="secondary">
                <Receipt />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: 'background.default', 
      px: { xs: 0.75, sm: 1, md: 1.5 }, 
      py: { xs: 2, sm: 2.5, md: 3 }, 
      maxWidth: '95vw', 
      overflowX: 'hidden' 
    }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C' }}>Expenses</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={handleRefresh}
                sx={{ minWidth: 'auto' }}
            >
                Refresh
            </Button>
            <Button
                variant="outlined"
                startIcon={<GetApp />}
                onClick={() => setExportDialogOpen(true)}
                sx={{ minWidth: 'auto' }}
            >
                Export
            </Button>
            <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => navigate('/admin/expenses/create')}
                sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' }, minWidth: 'auto' }}
            >
                Create Expense
            </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Filter Section */}
      <Card sx={{ mb: 3, backgroundColor: 'background.paper' }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Search"
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder="Search by title, ref..."
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                select
                fullWidth
                label="Status"
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                size="small"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
                <TextField
                    type="date"
                    fullWidth
                    label="Start Date"
                    name="expense_date_gte"
                    value={filters.expense_date_gte}
                    onChange={handleFilterChange}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
                <TextField
                    type="date"
                    fullWidth
                    label="End Date"
                    name="expense_date_lte"
                    value={filters.expense_date_lte}
                    onChange={handleFilterChange}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Button variant="contained" fullWidth onClick={handleApplyFilters} sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}>
                Apply
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ backgroundColor: 'background.paper' }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ 
            width: '100%',
            overflowX: 'auto',
            minHeight: 520,
            '&::-webkit-scrollbar': { height: 8 },
            '&::-webkit-scrollbar-track': { backgroundColor: 'rgba(0,0,0,0.1)', borderRadius: 4 },
            '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: 4 },
            '&::-webkit-scrollbar-thumb:hover': { backgroundColor: 'rgba(0,0,0,0.5)' },
          }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3, height: 400, alignItems: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <DataGrid
                rows={expenses}
                columns={columns}
                getRowId={(row) => row.id}
                pageSize={rowsPerPage}
                rowsPerPageOptions={[]}
                disableSelectionOnClick
                hideFooter
                sx={{
                  border: 'none',
                  minWidth: 1200,
                  '& .MuiDataGrid-columnHeader': {
                    backgroundColor: '#DC143C', // Using primary color directly or from theme if available
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                    color: 'white'
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: '#FFF0F3'
                  },
                  '& .MuiDataGrid-cell': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    padding: { xs: '8px 4px', sm: '8px 12px' }
                  }
                }}
            />
          )}
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mt: 2, backgroundColor: 'background.paper' }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                Page Total Amount
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {pageTotals.originalAmount}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                Page Total Amount (NGN)
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                NGN {formatAmount(pageTotals.amountInNgn)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                Page Total Amount (CNY)
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                CNY {formatAmount(pageTotals.amountInCny)}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Custom Pagination Bar */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: { xs: 'center', sm: 'flex-end' }, 
        mt: 3, 
        gap: 2,
        flexWrap: 'wrap'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={handlePrevPage} disabled={page === 0}>
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2">
            Page {totalPages === 0 ? 0 : page + 1} of {totalPages}
          </Typography>
          <IconButton onClick={handleNextPage} disabled={page >= totalPages - 1}>
            <ChevronRight />
          </IconButton>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">
            Rows per page
          </Typography>
          {!customRowsActive ? (
            <TextField
              select
              size="small"
              value={[10, 20, 50, 100].includes(rowsPerPage) ? rowsPerPage : 'custom'}
              onChange={handleRowsPerPageChange}
              sx={{ width: 100 }}
            >
              {[10, 20, 50, 100].map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
              <MenuItem value="custom">Custom</MenuItem>
            </TextField>
          ) : (
            <TextField
              size="small"
              value={customRowsInput}
              onChange={handleCustomRowsInputChange}
              onBlur={handleCustomRowsInputBlur}
              autoFocus
              sx={{ width: 100 }}
              inputProps={{ min: 1, style: { textAlign: 'center' } }}
              placeholder="Rows"
            />
          )}
        </Box>
      </Box>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)}>
        <DialogTitle>Export Expenses</DialogTitle>
        <DialogContent sx={{ minWidth: 300, mt: 1 }}>
            <Typography variant="body2" gutterBottom>
                Export will use current filters.
            </Typography>
            <TextField
                select
                fullWidth
                label="Format"
                value={exportFormat}
                onChange={(e) => setExportFormat(e.target.value)}
                margin="normal"
            >
                <MenuItem value="csv">CSV</MenuItem>
                <MenuItem value="excel">Excel</MenuItem>
            </TextField>
        </DialogContent>
        <DialogActions>
            <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleExport} variant="contained" sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}>Export</Button>
        </DialogActions>
      </Dialog>

      {/* Receipt Preview Dialog */}
      <Dialog 
        open={previewOpen} 
        onClose={handleClosePreview}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Receipt Preview
          <IconButton
            aria-label="close"
            onClick={handleClosePreview}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {previewUrl && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              {previewUrl.toLowerCase().endsWith('.pdf') ? (
                 <iframe src={previewUrl} title="Receipt PDF" width="100%" height="500px" style={{ border: 'none' }} />
              ) : (
                <img 
                  src={previewUrl} 
                  alt="Receipt" 
                  style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} 
                  onError={(e) => {
                    e.target.onerror = null; 
                    e.target.src = ''; 
                    // Could set an error state to show a message
                  }}
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview}>Close</Button>
          <Button href={previewUrl} target="_blank" rel="noopener noreferrer" variant="contained">
            Open in New Tab
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExpenseList;
